<template>
  <div class="editor-wrapper">
    <div class="editor-frame-container">
      <iframe
        width="100%"
        height="600px"
        title="design-editor"
        id="editorFrame"
        ref="editorFrame"
      ></iframe>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Ref, watch, inject } from 'vue';
import pinia from '../../core/pinia';
import {
  useConfigStore,
  useEditorStore,
  useLineItemStore,
  useVdpTableStore
} from '../../stores';
import { E_VARIABLE_SURFACE, type ICommandManager, type IProductItems, type ISaveProductResponse, type TDesignEditor, type TVariableItems } from '../../types/design-editor';
import emitter from '../../core/emitter';
import { E_EDITOR_CUSTOM_EVENT } from '../../types/custom-events.enums';
import { InnerVdpDataFormatter } from '../../utils/inner-vdp-data-formatter';
import loadDatasets from '../../utils/load-datasets';
import { arraysHaveSameKeys } from '../../utils/arrays-have-same-keys';
import { E_SCENARIO } from '../../types/line-item.types';
import { E_ITEM_TYPE } from '../../types/print-product-model.types';
import { getUpdatedDatasetRow } from '../../utils/get-updated-loaded-dataset-row';

interface IndexableCollection<T> {
  length: number;
  get(index: number): T;
}

type NestedItem = {
  id?: string;
  type?: E_ITEM_TYPE;
  items?: IndexableCollection<NestedItem>;
  _parentGroupItem?: Record<string, any>;
  name?: string;
  text?: string;
};

const configStore = useConfigStore(pinia);
const editorStore = useEditorStore(pinia);
const lineItemStore = useLineItemStore(pinia);
const vdpTableStore = useVdpTableStore(pinia);

let checkLoad = false;

const editorFrame: Ref<HTMLIFrameElement | null> = ref(null);
const editorRef: Ref<TDesignEditor | null> = ref(null);
const commandManagerRef: Ref<ICommandManager | null> = ref(null);
const variableItems: Ref<TVariableItems> = ref({ [E_VARIABLE_SURFACE.DEFAULT]: [] });

const iframeApiLoadStatus = inject<Ref<'not' | 'pending' | 'loaded'>>('iframeApiLoadStatus');

const isRestore = computed(() => editorStore.getIsRestore);
const productDefinition = computed(() => {
  const stateId = editorStore.getStateId;
  if (stateId) {
    return [stateId];
  } else {
    return configStore.getProductDefinition;
  }
});
const editorConfig = computed(() => configStore.getEditorConfig);

async function initDesignEditor(editorFrame: HTMLIFrameElement, productDefinition: string[] | string, editorConfig: any) {
  // --de-brand-primary: #FF705E;
  await CustomersCanvas.IframeApi.loadEditor(
    editorFrame,
    productDefinition,
    editorConfig
  ).then(async (editor) => {
    editorRef.value = editor;
    commandManagerRef.value = editor.commandManager as ICommandManager;
    await saveProduct();
    emitter.emit(E_EDITOR_CUSTOM_EVENT.EDITOR_LOAD);
  }).catch(error => {
    console.error(`The editor failed to load with an exception`, error);
  });
}

async function saveProduct() {
  const response = await editorRef.value.saveProduct(productDefinition.value) as ISaveProductResponse;
  setupDataset();
  editorStore.updateStateId(response.stateId);
  lineItemStore.updateLineItem({
    stateId: [response.stateId]
  });
  await vdpTableStore.putVdpInState();
  emitter.emit(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN_SUCCESS, response.stateId);
  return response;
}

// async function getProofImages() {
//   const response = await editorRef.value.getProofImages();
//   const proofImageUrls = response.proofImageUrls;
//   editorStore.setProofs(proofImageUrls);
//   lineItemStore.updateLineItem({
//     images: proofImageUrls,
//   });
//   return response;
// }

async function setupDataset() {
  const product = await editorRef.value.getProduct();
  variableItems.value = await product.getVariableItems();
  const vdpData = InnerVdpDataFormatter({
    vdpDataWithSurface: variableItems.value,
    revers: true,
    useOriginalValue: true,
  });
  
  const currentSurfaceData = vdpData.surfacesData;
  const beforeSurfaceData = vdpTableStore.getSurfacesData;

  if (!arraysHaveSameKeys<Record<string, string>[]>(currentSurfaceData.columnsTypes, beforeSurfaceData?.columnsTypes)) {
    console.log('* UPDATE TABLE! *');
    loadDatasets(vdpData.datasets, vdpData.surfacesData);
  } else {
    const surfaceIndex = 0;
    const rowIndex = 0;
    const datasets = getUpdatedDatasetRow(surfaceIndex, rowIndex, vdpData.datasets[surfaceIndex].rows[rowIndex]);
    if (datasets) {
      console.log('* UPDATE TABLE! * {@without-columns}');
      loadDatasets(datasets, vdpData.surfacesData);
    }
  }

  if (hasRowsDataInTable() && isRestore.value) {
    lineItemStore.updateLineItem({
      scenario: E_SCENARIO.VDP,
    });
  }
}

function hasRowsDataInTable() {
  const dataset = editorStore.getDataset;
  return dataset?.some((sheet) => !!sheet?.rows?.length)
}

async function getMappedItems() {
  const product = await editorRef.value.getProduct();
  
  let dirtyItems: Record<string, any>[] = (await product.getProductModel()).getAllItems();
  dirtyItems = dirtyItems?.filter((item) => item._parentContainer._name !== 'Mockup');

  const mappedItems = flattenItems(dirtyItems);
  return mappedItems;
}

async function getProductItems(): Promise<IProductItems> {
  const mappedItems = await getMappedItems();
  
  const placeholderItems = extractItemsByType(mappedItems, "PlaceholderItem");
  const boundedTextItems = extractItemsByType(mappedItems, "BoundedTextItem");
  const plainTextItems = extractItemsByType(mappedItems, "PlainTextItem");

  const productItems: IProductItems = {
    placeholderItems,
    boundedTextItems,
    plainTextItems,
  };

  emitter.emit(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS_SUCCESS, productItems);
  return productItems;
}

function flattenItems(items: IndexableCollection<NestedItem> | NestedItem[]): NestedItem[] {
  const toArray = (collection: IndexableCollection<NestedItem> | NestedItem[]): NestedItem[] => {
    if (Array.isArray(collection)) {
      return collection;
    }
    const result: NestedItem[] = [];
    for (let i = 0; i < collection.length; i++) {
      result.push(collection.get(i));
    }
    return result;
  };

  return toArray(items).flatMap(item => {
    if (item.items && item.items.length > 0) {
      return flattenItems(item.items);
    }
    return [item];
  });
}

function extractItemsByType(mappedItems: Record<string, any>[], type: "PlaceholderItem" | "BoundedTextItem" | "PlainTextItem") {
  return mappedItems.filter((item) => item.type === type);
}

async function getItemsGroup() {
  const mappedItems = await getMappedItems();
  
  const groupsSet = new Set(mappedItems.map((item) => {
    const group = item._parentGroupItem;
    return (group?.type === "LayoutItem") ? group : null;
  }).filter(Boolean));

  const groups = Array.from(groupsSet);

  if (groups?.length > 1) {
    console.warn('More than one group found!');
  }
  
  return (groups?.length) ? groups[0] : null;
}

async function getItemByName(itemName: string) {
  const mappedItems = await getMappedItems();
  return mappedItems.find((item) => item.name === itemName);
}

async function init() {
  if (editorFrame.value) {
    console.log('editor config:', editorConfig.value);
    await initDesignEditor(
      editorFrame.value,
      productDefinition.value as string[],
      editorConfig.value
    );
    emitter.on(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN, async () => {
      return await saveProduct();
    });
    emitter.on(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS, async () => {
      return await getProductItems();
    });
    emitter.on(E_EDITOR_CUSTOM_EVENT.REMOVE_ITEMS_GROUP, async () => {
      const group = await getItemsGroup();
      if (!group) return;
      const query = `#${group?.id}`;
      await commandManagerRef.value?.execute("deleteItems", {
        query,
        force: true, 
      });
      console.warn(`Item has been removed! Query: ${query}`);
    });
    emitter.on(E_EDITOR_CUSTOM_EVENT.EDIT_ITEM_TEXT, async ({ itemName, itemText }) => {
      const item = await getItemByName(itemName);
      if (!item) {
        console.warn(`[${E_EDITOR_CUSTOM_EVENT.EDIT_ITEM_TEXT}] Item not found!`);
        return;
      }
      if (item.text === itemText) {
        console.warn(`[${E_EDITOR_CUSTOM_EVENT.EDIT_ITEM_TEXT}] Item text has not changed`);
        return;
      }
      const query = `#${item.id}`;
      await commandManagerRef.value?.execute('editItems', {
        query,
        queryOptions: {
          flatGroupItems: true,
        },
        itemData: {
          text: itemText,
        },
        applyParams: {
          applyPropertiesDeep: true,
        },
      });
    })
    // if (!isRestore.value) {
    //   emitter.emit(E_EDITOR_CUSTOM_EVENT.REMOVE_ITEMS_GROUP);
    // }
  } else {
    console.error('Editor frame is not available.');
  }
}

if (!iframeApiLoadStatus) {
  console.error(`Injector iframeApiLoadStatus is ${iframeApiLoadStatus}`);
}

watch(iframeApiLoadStatus!, async (status) => {
  if (status === 'loaded' && !checkLoad) {
    checkLoad = true;
    await init();
  }
}, { immediate: true, deep: true });
</script>

<style scoped lang="scss">
.editor-frame-container {
  position: relative;
  width: 100%;
  height: 100%;
}
#editorFrame {
  border: none;
}
</style>